import {
  ArticleCategory,
  ArticleType,
  Currency,
  getCurrencyByPurchaseCountry,
  PurchaseCountry,
} from '@nemuru-eng/js-api-client';
import { v4 as uuidv4 } from 'uuid';

import { AutomobileItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/entities/AutomobileItem';
import { Item } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/entities/Item';
import { SINGLE_UNIT_ARTICLE_TYPES } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/hooks/useArticleTypes';
import { ItemMap } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/maps/ItemMap';
import { FormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/types/FormItem';
import { MinimalFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/types/MinimalFormItem';
import { StoredCart } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/types/StoredCart';

export class CartEntity {
  public readonly currency: Currency;

  private constructor(
    public readonly items: Item<FormItem>[],
    public readonly requiredArticleTypes: ArticleType[],
    public readonly purchaseCountry: PurchaseCountry,
  ) {
    this.currency = getCurrencyByPurchaseCountry(purchaseCountry);
  }

  public static initial(purchaseCountry: PurchaseCountry = PurchaseCountry.ESP): CartEntity {
    return new CartEntity([], [], purchaseCountry);
  }

  public static createCart(items: Item<FormItem>[], purchaseCountry: PurchaseCountry): CartEntity {
    return new CartEntity(items, [], purchaseCountry);
  }

  public static updateCartItemsFromMinimalForm(formItems: MinimalFormItem[], oldCart: CartEntity): CartEntity {
    const items = ItemMap.toItems(formItems, oldCart);
    return new CartEntity(items, oldCart.requiredArticleTypes, oldCart.purchaseCountry);
  }

  public static updateCartItemFromDialogForm(item: FormItem, index: number, oldCart: CartEntity): CartEntity {
    const newCartItem = ItemMap.fromDialogToItem(item, oldCart.items[index]?.totalDiscount);

    // Add additionalData if it existed in old cart item
    const oldCartItemAdditionalData = oldCart?.items[index]?.additionalData;
    if (oldCartItemAdditionalData) {
      newCartItem.additionalData = oldCartItemAdditionalData;
    }

    const cartItems = [...oldCart.items];
    cartItems[index] = newCartItem;

    return new CartEntity(cartItems, oldCart.requiredArticleTypes, oldCart.purchaseCountry);
  }

  public static removeCartItem(index: number, oldCart: CartEntity): CartEntity {
    const newItems = [...oldCart.items];
    newItems.splice(index, 1);
    return new CartEntity(newItems, oldCart.requiredArticleTypes, oldCart.purchaseCountry);
  }

  public static updateRequiredArticleTypes(requiredArticleTypes: ArticleType[], oldCart: CartEntity): CartEntity {
    return new CartEntity(oldCart.items, requiredArticleTypes, oldCart.purchaseCountry);
  }

  public static createNewCartFromStoredCart(cart: StoredCart): CartEntity {
    const newCart = new CartEntity(
      cart.cart.items.map((item) => ItemMap.fromDialogToItem(item.data)),
      cart.cart.requiredArticleTypes ?? [],
      cart.cart.purchaseCountry,
    );
    return newCart;
  }

  public validTotal(): boolean {
    return Number(this.total()) > 0;
  }

  public itemsAreCompleted(): boolean {
    return this.items.every((item) => item.isItemCompleted());
  }

  public hasCompetedItems(): boolean {
    return this.items.some((item) => item.isItemCompleted());
  }

  public getCompetedItems(): Item<FormItem>[] {
    return this.items.filter((item) => item.isItemCompleted());
  }

  public validArticleTypes(): boolean {
    if (this.items.filter((item) => SINGLE_UNIT_ARTICLE_TYPES.includes(item.data.type)).length > 1) return false;
    if (
      this.requiredArticleTypes.length > 0 &&
      !this.items.some((item) => this.requiredArticleTypes.includes(item.data.type))
    )
      return false;
    return true;
  }

  public validArticleCategories(): boolean {
    return this.items.some((item) => [ArticleCategory.PHYSICAL, ArticleCategory.DIGITAL].includes(item.data.category));
  }

  public isReadyToSubmit(): boolean {
    return this.validTotal() && this.itemsAreCompleted() && this.validArticleCategories() && this.validArticleTypes();
  }

  public missingRequiredArticleTypes(): ArticleType[] {
    const missingArticleTypes = this.requiredArticleTypes.filter(
      (requiredArticleType) =>
        !this.items.some((item) => item.data.type === requiredArticleType && item.isItemCompleted()),
    );
    return missingArticleTypes;
  }

  public cartItemsToMinimalForm(): MinimalFormItem[] {
    return this.items?.map((item) => item.toMinimalFormItem());
  }

  private cartIsEmpty(): boolean {
    return !this.items || this.items.length === 0;
  }

  public total(): number {
    if (this.cartIsEmpty()) return 0;
    let total = this.items
      .map((item) => Math.round(item.price * 100 * item.units) / 100)
      .reduce((prev, current) => {
        let newPrev = prev;
        let newCurrent = current;
        if (isNaN(prev)) newPrev = 0;
        if (isNaN(current)) newCurrent = 0;
        return newPrev + newCurrent;
      });
    if (isNaN(total)) total = 0;
    return Math.round(total * 100) / 100;
  }

  public totalVat(): number | undefined {
    if (this.cartIsEmpty()) return;
    let total = this.items
      .map((item) => {
        return (Math.round(item.price * 100 * item.units) / 100 / (1 + item.vatRate)) * item.vatRate;
      })
      .reduce((prev, current) => {
        let newPrev = prev;
        let newCurrent = current;
        if (isNaN(prev)) newPrev = 0;
        if (isNaN(current)) newCurrent = 0;
        return newPrev + newCurrent;
      });
    if (isNaN(total)) total = 0;
    return total;
  }

  public totalDiscount(): number {
    return this.items.reduce((acc, item) => acc + item.totalDiscount, 0);
  }

  public updateDiscount(discount: number): CartEntity {
    const items = this.items.map((item) => {
      if (item instanceof AutomobileItem) {
        return item.updateDiscount(discount);
      }
      return item;
    });

    return new CartEntity(items, this.requiredArticleTypes, this.purchaseCountry);
  }

  public toDto() {
    return {
      items: this.items.map((item) => item.toItemDto()),
      totalPriceWithTax: this.total() - this.totalDiscount(),
      currency: this.currency,
      reference: uuidv4(),
    };
  }

  public cartArticleType(): ArticleType | undefined {
    const candidateItem = this.items.reduce(
      (acc, item) => (acc.totalPrice > item.totalPrice ? acc : item),
      this.items[0],
    );
    if (!candidateItem) return;
    return candidateItem.data?.type;
  }
}
